/**
 * File: admin.ts
 * Path: packages/config/src/admin.ts
 * Description: Finalized admin navigation configuration with updated toolsets and root settings.
 */

import type { Permission } from "@idk/types";

export interface AdminNavItem {
  label: string;
  href?: string;
  icon?: string;
  /** Hidden unless the signed-in role has this permission. */
  requires?: Permission;
  /** One line under the label on a section's own page (phones). */
  description?: string;
  /** A group's own page (the phone bottom bar opens it), listing its links. */
  hub?: string;
  children?: AdminNavItem[];
}

export const ADMIN_PATHS = {
  dashboard: "/dashboard",
  login: "/login",
  portfolio: {
    root: "/portfolio",
    new: "/portfolio/new",
    edit: (id: string) => `/portfolio/${id}`,
    preview: (id: string) => `/portfolio/preview/${id}`,
  },
  academy: {
    root: "/academy",
    new: "/academy/new",
    edit: (id: string) => `/academy/${id}`,
    preview: (id: string) => `/academy/preview/${id}`,
  },
  services: {
    root: "/services",
    // The registry is defined in code, so a service is inspected, not edited.
    detail: (id: string) => `/services/${id}`,
  },
  /** The leads pipeline and the Google Maps search. */
  leads: {
    root: "/leads",
    find: "/leads/find",
    /** Where a lead's page used to live; redirects to `businesses.detail`. */
    legacyDetail: (id: string) => `/leads/${id}`,
    /** Where the page used to live; redirects to `root`. */
    legacy: "/tools/leads",
  },
  /** Referral partners and their payouts. */
  referrals: {
    root: "/referrals",
    /** Where the page used to live, under Tools; redirects to `root`. */
    legacy: "/tools/referrals",
  },
  /** Won work: the list of clients. */
  clients: {
    root: "/clients",
    /** Where a client's page used to live; redirects to `businesses.detail`. */
    legacyDetail: (id: string) => `/clients/${id}`,
  },
  /** One page per business, lead or client: the tabs shown depend on its stage. */
  businesses: {
    detail: (id: string) => `/businesses/${id}`,
  },
  /** The jobs built for clients: a board by stage, and one page per job. */
  projects: {
    root: "/projects",
    detail: (clientId: string, jobId: string) => `/projects/${clientId}/${jobId}`,
  },
  /** Win rate, cost to win a client, money owed and the rest, by period. */
  numbers: {
    root: "/numbers",
    glossary: "/numbers/glossary",
  },
  /** What clients owe, the invoices sent and the agreements behind them (tabs Owed, Invoices, Agreements). */
  money: "/money",
  /** One invoice on its own page: its number ("INV-000012"), or a draft's id. */
  invoice: (key: string) => `/money/invoices/${key}`,
  /** Where Money owed used to live; redirects to `money`. */
  moneyOwed: "/money-owed",
  /** The old CRM Settings address (and its ?tab= links); redirects to the matching tab of `settings`. */
  crmSettings: {
    root: "/crm-settings",
    payment: "/settings?tab=payment",
    imports: "/settings?tab=imports",
    templates: "/settings?tab=messages",
    consent: "/settings?tab=consent",
  },
  /** Care plans: who pays monthly, when each plan runs out, and the support asked for. */
  carePlans: {
    root: "/care-plans",
    detail: (id: string) => `/care-plans/${id}`,
  },
  /** Where Care plans used to live (Retainers); redirects to `carePlans.root`. */
  retainers: "/retainers",
  /** Agreements: drafts, signing links, countersigning and payment details. */
  contracts: {
    root: "/contracts",
    new: "/contracts/new",
    /** A contract signed elsewhere (Zoho, Google Docs), brought in with its old signed PDF. */
    import: "/contracts/import",
    /** Several old signed contracts at once, from a CSV and their PDFs. */
    importBulk: "/contracts/import/bulk",
    detail: (id: string) => `/contracts/${id}`,
    /** Where the payment details used to live; redirects to `crmSettings.payment`. */
    settings: "/contracts/settings",
    /** An invoice for a contract payment (?contractId=…&payment=<reference>), with a live PDF preview. */
    newInvoice: "/contracts/invoice/new",
  },
  /** Every saved quote, and the quote builder that makes a new one. */
  quotes: {
    root: "/quotes",
    /** The quote builder: lines from the price list. */
    new: "/quotes/new",
    /** A sent quote opened in the builder: saving makes its next version. */
    edit: (id: string) => `/quotes/${id}/edit`,
    /** The questionnaire, sent as an estimate to someone Liam hasn't met. */
    estimate: "/quotes/estimate",
    /** Where the quote builder used to live, under Tools; redirects to `new`. */
    legacy: "/tools/quote",
  },
  /** The books: one set for the company and one per person (accounting). */
  accounting: {
    root: "/accounting",
    /** Plain-language home: the four figures and the everyday entries. */
    overview: "/accounting/overview",
    journals: "/accounting/journals",
    accounts: "/accounting/accounts",
    budgets: "/accounting/budgets",
    reports: "/accounting/reports",
    /** Tax and filings: the overview, with one page per tax beneath it. */
    tax: "/accounting/tax",
    taxIncome: "/accounting/tax/income",
    taxProvisional: "/accounting/tax/provisional",
    taxOther: "/accounting/tax/other",
    /** CIPC annual return and the beneficial ownership declaration. */
    taxCompany: "/accounting/tax/company",
    /** The old CIPC address; redirects to `taxCompany`. */
    cipc: "/accounting/cipc",
    setup: "/accounting/setup",
    /** The old How to page; redirects to the start page (Today). */
    guide: "/accounting/guide",
  },
  tools: {
    /** The old tools index page; redirects to the dashboard. */
    root: "/tools",
    audit: "/tools/audit",
    brand: "/tools/brand",
  },
  /** Phone section pages: the bottom bar opens one per group, listing its links. */
  menu: {
    crm: "/menu/crm",
    accounting: "/menu/accounting",
    cms: "/menu/cms",
  },
  /** Search and AI readiness across every case study and article. */
  seo: "/seo",
  /** Google reviews, review links and per-project review requests. */
  reviews: "/reviews",
  /** The bell: every push notification this person was sent. */
  notifications: "/notifications",
  /** Whether notifications and the admin are working (System > Diagnostics). */
  diagnostics: "/diagnostics",
  /** One Settings page: tabs Profile, Company, Getting paid, Sales, Messages, Imports, Consent log, Users, Books setup (?tab=). */
  settings: "/settings",
  /** Every tutorial in the admin; reached only from the "All tutorials" link at the foot of a Help dialog. */
  tutorials: "/help/tutorials",
} as const;

export const SIDEBAR_ITEMS: AdminNavItem[] = [
  {
    label: "Today",
    href: ADMIN_PATHS.dashboard,
    icon: "Home",
  },
  {
    label: "CRM",
    icon: "Users",
    hub: ADMIN_PATHS.menu.crm,
    children: [
      {
        label: "Pipeline",
        href: ADMIN_PATHS.leads.root,
        icon: "Users",
        description: "Everyone you are talking to, and who to call next",
        requires: "sales",
      },
      {
        label: "Clients",
        href: ADMIN_PATHS.clients.root,
        icon: "Handshake",
        description: "Past and current clients, projects and what is still owed",
        requires: "sales",
      },
      {
        label: "Projects",
        href: ADMIN_PATHS.projects.root,
        icon: "Briefcase",
        description: "The jobs you are building for clients, from planning to handed over",
        requires: "sales",
      },
      {
        label: "Care plans",
        href: ADMIN_PATHS.carePlans.root,
        icon: "Repeat",
        description: "Who pays you every month, when each plan ends, and the support asked for",
        requires: "sales",
      },
      {
        label: "Money",
        href: ADMIN_PATHS.money,
        icon: "Banknote",
        description: "What clients owe you, the invoices you sent and the agreements behind them",
        requires: "sales",
      },
      {
        label: "Referrals",
        href: ADMIN_PATHS.referrals.root,
        icon: "Share2",
        description: "Partners who send you work, and what you owe them",
        requires: "referrals",
      },
    ],
  },
  {
    label: "Accounting",
    icon: "Wallet",
    hub: ADMIN_PATHS.menu.accounting,
    children: [
      {
        label: "Overview",
        href: ADMIN_PATHS.accounting.overview,
        icon: "Wallet",
        description: "Where the money stands right now",
        requires: "finance",
      },
      {
        label: "Transactions",
        href: ADMIN_PATHS.accounting.journals,
        icon: "BookOpen",
        description: "Every entry in the books, and add a new one",
        requires: "finance",
      },
      {
        label: "Accounts",
        href: ADMIN_PATHS.accounting.accounts,
        icon: "ListTree",
        description: "The list of accounts the books use",
        requires: "finance",
      },
      {
        label: "Budgets",
        href: ADMIN_PATHS.accounting.budgets,
        icon: "Target",
        description: "Budget against actual, and the forecast",
        requires: "finance",
      },
      {
        label: "Reports",
        href: ADMIN_PATHS.accounting.reports,
        icon: "Scale",
        description: "Profit and loss, balance sheet",
        requires: "finance",
      },
      {
        label: "Tax and filings",
        href: ADMIN_PATHS.accounting.tax,
        icon: "Landmark",
        description: "Every tax and filing: returns, provisional tax, VAT, PAYE, CIPC",
        requires: "finance",
      },
      {
        label: "Setup",
        href: ADMIN_PATHS.accounting.setup,
        icon: "SlidersHorizontal",
        description: "Getting the books ready: details, opening balances, Zoho import, lock",
        requires: "finance",
      },
    ],
  },
  {
    label: "CMS",
    icon: "FolderOpen",
    hub: ADMIN_PATHS.menu.cms,
    children: [
      {
        label: "Portfolio",
        href: ADMIN_PATHS.portfolio.root,
        icon: "FolderOpen",
        description: "Case studies shown on the website",
        requires: "content",
      },
      {
        label: "Academy",
        href: ADMIN_PATHS.academy.root,
        icon: "GraduationCap",
        description: "Articles shown on the website",
        requires: "content",
      },
      {
        label: "Services",
        href: ADMIN_PATHS.services.root,
        icon: "Package",
        description: "What you sell, with prices and descriptions",
        requires: "content",
      },
      {
        label: "Reviews",
        href: ADMIN_PATHS.reviews,
        icon: "Star",
        description: "Google reviews and review links",
        requires: "content",
      },
      {
        label: "Search & AI",
        href: ADMIN_PATHS.seo,
        icon: "Sparkles",
        description: "How ready each page is for Google and AI answers",
        requires: "content",
      },
    ],
  },

  {
    label: "Tools",
    children: [
      {
        label: "Website audit",
        href: ADMIN_PATHS.tools.audit,
        icon: "Search",
        requires: "tools",
      },
      {
        label: "Brand Kit",
        href: ADMIN_PATHS.tools.brand,
        icon: "Palette",
        requires: "tools",
      },
    ],
  },
  {
    label: "System",
    children: [
      {
        label: "Notifications",
        href: ADMIN_PATHS.notifications,
        icon: "Bell",
      },
      {
        label: "Diagnostics",
        href: ADMIN_PATHS.diagnostics,
        icon: "Activity",
      },
      {
        label: "Settings",
        href: ADMIN_PATHS.settings,
        icon: "Settings",
      },
    ],
  },
];

/**
 * The menu a role sees: items it lacks the permission for are dropped, and a
 * group left with no links is dropped with them.
 */
export const visibleNavItems = (
  items: AdminNavItem[],
  can: (permission: Permission) => boolean,
): AdminNavItem[] =>
  items.flatMap((item) => {
    if (item.requires && !can(item.requires)) return [];
    if (!item.children) return [item];
    const children = visibleNavItems(item.children, can);
    return children.length > 0 ? [{ ...item, children }] : [];
  });
